import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { usePlanNavigation } from "@/hooks/usePlanNavigation";
import { supabase } from "@/integrations/supabase/client";
import { Check, CreditCard, Coins, Infinity, Users, Mail, MessageSquare, X, HardDrive } from "lucide-react";
import CouponRedeemField from "@/components/CouponRedeemField";

export const PlansSection = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { navigateToPlan } = usePlanNavigation();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [creditLoading, setCreditLoading] = useState<string | null>(null);
  const [storageLoading, setStorageLoading] = useState<string | null>(null);
  const [basicBillingInterval, setBasicBillingInterval] = useState<'month' | 'year'>('month');
  const [proBillingInterval, setProBillingInterval] = useState<'month' | 'year'>('month');
  const [premiumBillingInterval, setPremiumBillingInterval] = useState<'month' | 'year'>('month');
  
  const isHomePage = location.pathname === '/';

  // Buscar dados da assinatura atual
  const { data: subscriptionData } = useQuery({
    queryKey: ["subscription"],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase.functions.invoke("check-subscription");
      
      if (error) {
        console.error("Erro ao buscar assinatura:", error);
        return null;
      }
      
      return data;
    },
    enabled: !!user,
  });

  const handleCreateCheckout = async (planType: 'basic' | 'pro' | 'premium', billingInterval?: 'month' | 'year') => {
    if (!user) {
      navigateToPlan(planType);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { planType, billingInterval: billingInterval || 'month' }
      });

      if (error) {
        toast({
          title: "Erro",
          description: "Erro ao criar sessão de checkout. Tente novamente.",
          variant: "destructive",
        });
        return;
      }

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Erro ao criar checkout:', error);
      toast({
        title: "Erro",
        description: "Erro ao processar pagamento. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCreditCheckout = async (packageType: 'basic' | 'medium' | 'large') => {
    if (!user) {
      navigateToPlan();
      return;
    }

    setCreditLoading(packageType);
    try {
      const { data, error } = await supabase.functions.invoke('create-credit-checkout', {
        body: { packageType }
      });

      if (error) {
        toast({
          title: "Erro",
          description: "Erro ao criar sessão de checkout. Tente novamente.",
          variant: "destructive",
        });
        return;
      }

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Erro ao criar checkout de créditos:', error);
      toast({
        title: "Erro",
        description: "Erro ao processar pagamento. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setCreditLoading(null);
    }
  };

  const handleCreateStorageCheckout = async (packageType: 'small' | 'medium' | 'large') => {
    if (!user) {
      navigateToPlan();
      return;
    }

    setStorageLoading(packageType);
    try {
      const { data, error } = await supabase.functions.invoke('create-storage-checkout', {
        body: { packageType }
      });

      if (error) {
        toast({
          title: "Erro",
          description: "Erro ao criar sessão de checkout. Tente novamente.",
          variant: "destructive",
        });
        return;
      }

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Erro ao criar checkout de storage:', error);
      toast({
        title: "Erro",
        description: "Erro ao processar pagamento. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setStorageLoading(null);
    }
  };

  const currentPlan = subscriptionData?.plan || 'free';

  return (
    <div className="container max-w-6xl mx-auto py-8 px-4">
      <div className="flex items-center mb-8"></div>
      
      <CouponRedeemField />

      <div className="text-center mb-12">
        <h2 className="text-5xl md:text-7xl font-bold mb-4 text-white">Escolha o plano ideal para você</h2>
        <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">Selecione o plano que melhor atende às suas necessidades e tenha uma experiência personalizada.</p>
        <div className="mt-4 bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-3 max-w-2xl mx-auto">
          <p className="text-yellow-300 font-medium text-sm">⚠️ IMPORTANTE: Todas as ferramentas da Super IA Jurídica utilizam créditos. Consulte seu saldo para aproveitar ao máximo.</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
        {/* Plano Gratuito */}
        <div className="rounded-lg border shadow-sm bg-gray-800 border-gray-700 text-white transition-all duration-700 delay-200 animate-slide-up-delay-1 min-w-0">
          <div className="flex flex-col space-y-1.5 p-6">
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-semibold leading-none tracking-tight text-white">Plano Gratuito</h3>
              {currentPlan === 'free' && !isHomePage && (
                <Badge className="bg-green-600 text-white hover:bg-green-600">Atual</Badge>
              )}
            </div>
            <p className="text-sm text-gray-400">Para sempre - sem prazo de validade</p>
          </div>
          <div className="p-6 pt-0">
            <div className="space-y-4">
              <div className="flex items-baseline">
                <span className="text-3xl font-bold text-white">R$0</span>
                <span className="text-gray-400 ml-2">/mês</span>
              </div>
              <div className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm font-medium text-center">✓ Permanente - Não é Teste!</div>
              <div className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-lg font-bold text-center flex items-center justify-center">
                <Coins className="h-5 w-5 mr-2" />
                10K créditos/mês
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center text-gray-300">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>Uso individual</span>
                </li>
                <li className="flex items-center text-gray-300">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>10 MB de armazenamento</span>
                </li>
                <li className="flex items-center text-gray-300">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>3 mensagens por dia no chat</span>
                </li>
                <li className="flex items-center text-gray-300">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>Papel timbrado Personalizado</span>
                </li>
                <li className="flex items-center text-gray-300">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>Memória do chat de 30 dias</span>
                </li>
                <li className="flex items-center text-gray-300">
                  <X className="h-3 w-3 mr-2 text-red-500 flex-shrink-0" />
                  <span>Compartilhamento: Não disponível</span>
                </li>
                <li className="flex items-center text-gray-300">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>Suporte por WhatsApp</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="flex items-center p-6 pt-0">
            <Button 
              variant={currentPlan === 'free' && !isHomePage ? "default" : "outline"}
              className={currentPlan === 'free' && !isHomePage ? "w-full bg-green-600 text-white hover:bg-green-600" : "w-full bg-accent text-accent-foreground hover:bg-accent hover:text-accent-foreground"}
              onClick={isHomePage ? () => navigateToPlan('free') : undefined}
              disabled={currentPlan === 'free' && !isHomePage}
            >
              {currentPlan === 'free' && !isHomePage ? 'Plano Atual' : 'Começar Gratuitamente'}
            </Button>
          </div>
        </div>

        {/* Plano Básico */}
        <div className="rounded-lg border text-card-foreground shadow-sm bg-primary border-primary relative overflow-hidden transition-all duration-700 delay-400 animate-scale-in-delay-1 min-w-0">
          <div className="absolute top-0 right-0 bg-yellow-500 text-black px-3 py-1 text-sm font-bold">⭐ RECOMENDADO</div>
          <div className="absolute top-0 left-0 bg-red-600 text-white px-3 py-1 text-sm font-bold animate-pulse">🔥 50% OFF</div>
          <div className="flex flex-col space-y-1.5 p-6 pt-8">
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-semibold leading-none tracking-tight text-white">Plano Básico</h3>
              {currentPlan === 'basic' && !isHomePage && (
                <Badge className="bg-green-600 text-white hover:bg-green-600">Atual</Badge>
              )}
            </div>
            <p className="text-sm text-blue-100">Funcionalidades essenciais para uso diário</p>
            <Badge className="bg-red-600/90 text-white hover:bg-red-600 animate-pulse w-fit text-xs">⏰ OFERTA POR TEMPO LIMITADO</Badge>
          </div>
          <div className="p-6 pt-0">
            <div className="space-y-4">
              {/* Toggle Mensal / Anual */}
              <div className="flex items-center bg-blue-900/50 rounded-full p-1 gap-1">
                <button
                  onClick={() => setBasicBillingInterval('month')}
                  className={`flex-1 text-xs font-semibold py-1.5 rounded-full transition-all ${basicBillingInterval === 'month' ? 'bg-white text-primary shadow' : 'text-blue-200 hover:text-white'}`}
                >
                  Mensal
                </button>
                <button
                  onClick={() => setBasicBillingInterval('year')}
                  className={`flex-1 text-xs font-semibold py-1.5 rounded-full transition-all ${basicBillingInterval === 'year' ? 'bg-white text-primary shadow' : 'text-blue-200 hover:text-white'}`}
                >
                  Anual
                  <span className="ml-1 text-[10px] text-green-400 font-bold">-10%</span>
                </button>
              </div>

              {basicBillingInterval === 'month' ? (
                <div className="flex items-baseline gap-2">
                  <span className="text-lg text-blue-200 line-through">R$89</span>
                  <span className="text-3xl font-bold text-white">R$44,50</span>
                  <span className="text-blue-100">/mês</span>
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg text-blue-200 line-through">R$44,50</span>
                    <span className="text-3xl font-bold text-white">R$39,90</span>
                    <span className="text-blue-100">/mês</span>
                  </div>
                  <span className="text-xs text-blue-200">R$478,80/ano (pagamento único ou parcelado)</span>
                </div>
              )}
              <div className="bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full text-lg font-bold text-center flex items-center justify-center">
                <Coins className="h-5 w-5 mr-2" />
                500K créditos/mês
              </div>
              
              <ul className="space-y-2 text-sm">
                <li className="flex items-center text-blue-50">
                  <Check className="h-3 w-3 mr-2 text-green-400 flex-shrink-0" />
                  <span>Uso individual</span>
                </li>
                <li className="flex items-center text-blue-50">
                  <Check className="h-3 w-3 mr-2 text-green-400 flex-shrink-0" />
                  <span>100 MB de armazenamento</span>
                </li>
                <li className="flex items-center text-blue-50">
                  <Check className="h-3 w-3 mr-2 text-green-400 flex-shrink-0" />
                  <span>Chat com Super IA Jurídica</span>
                </li>
                <li className="flex items-center text-blue-50">
                  <Check className="h-3 w-3 mr-2 text-green-400 flex-shrink-0" />
                  <span>Papel timbrado Personalizado</span>
                </li>
                <li className="flex items-center text-blue-50">
                  <Check className="h-3 w-3 mr-2 text-green-400 flex-shrink-0" />
                  <span>Memória do chat de 90 dias</span>
                </li>
                <li className="flex items-center text-blue-50">
                  <Check className="h-3 w-3 mr-2 text-green-400 flex-shrink-0" />
                  <span>Compartilhamento: 1 pessoa</span>
                </li>
                <li className="flex items-center text-blue-50">
                  <Check className="h-3 w-3 mr-2 text-green-400 flex-shrink-0" />
                  <span className="whitespace-nowrap">+ 220 Modelos de Documentos</span>
                </li>
                <li className="flex items-center text-blue-50">
                  <Check className="h-3 w-3 mr-2 text-green-400 flex-shrink-0" />
                  <span className="whitespace-nowrap">Cofre de Documentos/Peças</span>
                </li>
                <li className="flex items-center text-blue-50">
                  <Check className="h-3 w-3 mr-2 text-green-400 flex-shrink-0" />
                  <span className="whitespace-nowrap">Editor de Documentos com IA</span>
                </li>
                <li className="flex items-center text-blue-50">
                  <Check className="h-3 w-3 mr-2 text-green-400 flex-shrink-0" />
                  <span className="whitespace-nowrap">Melhorar Documento com IA</span>
                </li>
                <li className="flex items-center text-blue-50">
                  <Check className="h-3 w-3 mr-2 text-green-400 flex-shrink-0" />
                  <span className="whitespace-nowrap">Reescreva Documentos com IA</span>
                </li>
                <li className="flex items-center text-blue-50">
                  <Check className="h-3 w-3 mr-2 text-green-400 flex-shrink-0" />
                  <div className="flex flex-col">
                    <span>Resumo de Documentos</span>
                    <span className="text-xs text-blue-200">máx. de 300 páginas por envio</span>
                  </div>
                </li>
                <li className="flex items-center text-blue-50">
                  <Check className="h-3 w-3 mr-2 text-green-400 flex-shrink-0" />
                  <span>Suporte por WhatsApp</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="flex items-center p-6 pt-0">
            <Button 
              variant={currentPlan === 'basic' && !isHomePage ? "default" : "outline"}
              className={currentPlan === 'basic' && !isHomePage ? "w-full bg-green-600 text-white" : "w-full bg-white text-primary"}
              onClick={isHomePage ? () => navigateToPlan('basic') : () => handleCreateCheckout('basic', basicBillingInterval)}
              disabled={(currentPlan === 'basic' && !isHomePage) || isLoading}
            >
              <CreditCard className="mr-2 h-4 w-4" />
              {currentPlan === 'basic' && !isHomePage ? 'Plano Atual' : (basicBillingInterval === 'year' ? 'Assinar Plano Anual' : 'Assinar Agora')}
            </Button>
          </div>
        </div>

        {/* Plano Pro */}
        <div className="rounded-lg border shadow-sm bg-gray-800 border-gray-700 text-white transition-all duration-700 delay-600 animate-slide-up-delay-2 relative overflow-hidden min-w-0">
          <div className="absolute top-0 left-0 bg-red-600 text-white px-3 py-1 text-sm font-bold animate-pulse rounded-br-lg">🔥 50% OFF</div>
          <div className="flex flex-col space-y-1.5 p-6 pt-8">
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-semibold leading-none tracking-tight text-white">Plano Pro</h3>
              {currentPlan === 'pro' && !isHomePage && (
                <Badge className="bg-green-600 text-white hover:bg-green-600">Atual</Badge>
              )}
            </div>
            <p className="text-sm text-gray-400">Acesso ilimitado a todas as funcionalidades</p>
            <Badge className="bg-red-600/90 text-white hover:bg-red-600 animate-pulse w-fit text-xs">⏰ OFERTA POR TEMPO LIMITADO</Badge>
          </div>
          <div className="p-6 pt-0">
            <div className="space-y-4">
              {/* Toggle Mensal / Anual */}
              <div className="flex items-center bg-gray-900/60 rounded-full p-1 gap-1">
                <button
                  onClick={() => setProBillingInterval('month')}
                  className={`flex-1 text-xs font-semibold py-1.5 rounded-full transition-all ${proBillingInterval === 'month' ? 'bg-white text-primary shadow' : 'text-gray-300 hover:text-white'}`}
                >
                  Mensal
                </button>
                <button
                  onClick={() => setProBillingInterval('year')}
                  className={`flex-1 text-xs font-semibold py-1.5 rounded-full transition-all ${proBillingInterval === 'year' ? 'bg-white text-primary shadow' : 'text-gray-300 hover:text-white'}`}
                >
                  Anual
                  <span className="ml-1 text-[10px] text-green-400 font-bold">-10%</span>
                </button>
              </div>

              {proBillingInterval === 'month' ? (
                <div className="flex items-baseline gap-2">
                  <span className="text-lg text-gray-500 line-through">R$149</span>
                  <span className="text-3xl font-bold text-white">R$74,50</span>
                  <span className="text-gray-400">/mês</span>
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg text-gray-500 line-through">R$74,50</span>
                    <span className="text-3xl font-bold text-white">R$67,05</span>
                    <span className="text-gray-400">/mês</span>
                  </div>
                  <span className="text-xs text-gray-400">R$804,60/ano (pagamento único ou parcelado)</span>
                </div>
              )}
              <div className="bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full text-lg font-bold text-center flex items-center justify-center">
                <Coins className="h-5 w-5 mr-2" />
                1M créditos/mês
              </div>
              
              <ul className="space-y-2 text-sm">
                <li className="flex items-center text-gray-300">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>Uso individual</span>
                </li>
                <li className="flex items-center text-gray-300">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>1 GB de armazenamento</span>
                </li>
                <li className="flex items-center text-gray-300">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>Papel timbrado Personalizado</span>
                </li>
                <li className="flex items-center text-gray-300">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>Suporte por WhatsApp</span>
                </li>
                <li className="flex items-center text-white">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>Memória do chat de 365 dias</span>
                </li>
                <li className="flex items-center text-white">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>Compartilhamento: até 10 pessoas</span>
                </li>
                <li className="flex items-center text-white">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>Chat com Super IA Jurídica</span>
                </li>
                <li className="flex items-center text-white">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span className="whitespace-nowrap">+ 220 Modelos de Documentos</span>
                </li>
                <li className="flex items-center text-white">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span className="whitespace-nowrap">Cofre de Documentos/Peças</span>
                </li>
                <li className="flex items-center text-white">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span className="whitespace-nowrap">Editor de Documentos com IA</span>
                </li>
                <li className="flex items-center text-white">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span className="whitespace-nowrap">Melhorar Documento com IA</span>
                </li>
                <li className="flex items-center text-white">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span className="whitespace-nowrap">Reescreva Documentos com IA</span>
                </li>
                <li className="flex items-center text-white">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <div className="flex flex-col">
                    <span>Resumo de Documentos</span>
                    <span className="text-xs text-white">máx. de 2000 páginas por envio</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div className="flex items-center p-6 pt-0">
            <Button 
              variant={currentPlan === 'pro' && !isHomePage ? "default" : "outline"}
              className={currentPlan === 'pro' && !isHomePage ? "w-full bg-green-600 text-white" : "w-full bg-primary text-white"}
              onClick={isHomePage ? () => navigateToPlan('pro') : () => handleCreateCheckout('pro', proBillingInterval)}
              disabled={(currentPlan === 'pro' && !isHomePage) || isLoading}
            >
              <CreditCard className="mr-2 h-4 w-4" />
              {currentPlan === 'pro' && !isHomePage ? 'Plano Atual' : (proBillingInterval === 'year' ? 'Assinar Plano Anual' : 'Assinar Agora')}
            </Button>
          </div>
        </div>

        {/* Plano Premium */}
        <div className="rounded-lg border shadow-sm bg-gray-800 border-gray-700 text-white transition-all duration-700 delay-800 animate-scale-in-delay-2 relative overflow-hidden min-w-0" style={{borderImage: "linear-gradient(45deg, rgb(245, 158, 11), rgb(234, 88, 12)) 1 / 1 / 0 stretch"}}>
          <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-yellow-500/20 to-orange-500/20 opacity-10 pointer-events-none"></div>
          <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-500 to-orange-500 text-black px-3 py-1 text-sm font-bold">PREMIUM</div>
          <div className="absolute top-0 left-0 bg-red-600 text-white px-3 py-1 text-sm font-bold animate-pulse rounded-br-lg">🔥 50% OFF</div>
          <div className="flex flex-col space-y-1.5 p-6 pt-8 relative">
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-semibold leading-none tracking-tight text-white">Plano Premium</h3>
              {currentPlan === 'premium' && !isHomePage && (
                <Badge className="bg-green-600 text-white hover:bg-green-600">Atual</Badge>
              )}
            </div>
            <p className="text-sm text-gray-400">Acesso empresarial com IA avançada</p>
            <Badge className="bg-red-600/90 text-white hover:bg-red-600 animate-pulse w-fit text-xs">⏰ OFERTA POR TEMPO LIMITADO</Badge>
          </div>
          <div className="p-6 pt-0 relative">
            <div className="space-y-4">
              {/* Toggle Mensal / Anual */}
              <div className="flex items-center bg-gray-900/60 rounded-full p-1 gap-1">
                <button
                  onClick={() => setPremiumBillingInterval('month')}
                  className={`flex-1 text-xs font-semibold py-1.5 rounded-full transition-all ${premiumBillingInterval === 'month' ? 'bg-white text-primary shadow' : 'text-gray-300 hover:text-white'}`}
                >
                  Mensal
                </button>
                <button
                  onClick={() => setPremiumBillingInterval('year')}
                  className={`flex-1 text-xs font-semibold py-1.5 rounded-full transition-all ${premiumBillingInterval === 'year' ? 'bg-white text-primary shadow' : 'text-gray-300 hover:text-white'}`}
                >
                  Anual
                  <span className="ml-1 text-[10px] text-green-400 font-bold">-10%</span>
                </button>
              </div>

              {premiumBillingInterval === 'month' ? (
                <div className="flex items-baseline gap-2">
                  <span className="text-lg text-gray-500 line-through">R$249</span>
                  <span className="text-3xl font-bold text-white">R$124,50</span>
                  <span className="text-gray-400">/mês</span>
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg text-gray-500 line-through">R$124,50</span>
                    <span className="text-3xl font-bold text-white">R$112,05</span>
                    <span className="text-gray-400">/mês</span>
                  </div>
                  <span className="text-xs text-gray-400">R$1.344,60/ano (pagamento único ou parcelado)</span>
                </div>
              )}
              <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-300 px-3 py-1 rounded-full text-lg font-bold text-center flex items-center justify-center">
                <Coins className="h-5 w-5 mr-2" />
                2M créditos/mês
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center text-gray-300">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>Uso individual</span>
                </li>
                <li className="flex items-center text-gray-300">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>2 GB de armazenamento</span>
                </li>
                <li className="flex items-center text-gray-300">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>Papel timbrado Personalizado</span>
                </li>
                <li className="flex items-center text-gray-300">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>Suporte por WhatsApp</span>
                </li>
                <li className="flex items-center text-gray-300">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>Memória do chat de 365 dias</span>
                </li>
                <li className="flex items-center text-white">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span className="flex items-center">
                    Compartilhamento: <Infinity className="h-4 w-4 mx-1" /> Ilimitado
                  </span>
                </li>
                <li className="flex items-center text-white">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span>Chat com Super IA Jurídica</span>
                </li>
                <li className="flex items-center text-white">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span className="whitespace-nowrap">+ 220 Modelos de Documentos</span>
                </li>
                <li className="flex items-center text-white">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span className="whitespace-nowrap">Cofre de Documentos/Peças</span>
                </li>
                <li className="flex items-center text-white">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span className="whitespace-nowrap">Editor de Documentos com IA</span>
                </li>
                <li className="flex items-center text-white">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span className="whitespace-nowrap">Melhorar Documento com IA</span>
                </li>
                <li className="flex items-center text-white">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <span className="whitespace-nowrap">Reescreva Documentos com IA</span>
                </li>
                <li className="flex items-center text-white">
                  <Check className="h-3 w-3 mr-2 text-green-500 flex-shrink-0" />
                  <div className="flex flex-col">
                    <span>Resumo de Documentos</span>
                    <span className="text-xs text-white">máx. de 5000 páginas por envio</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div className="flex items-center p-6 pt-0 relative">
            <Button 
              variant={currentPlan === 'premium' && !isHomePage ? "default" : "outline"}
              className={currentPlan === 'premium' && !isHomePage ? "w-full bg-green-600 text-white" : "w-full bg-primary text-white"}
              onClick={isHomePage ? () => navigateToPlan('premium') : () => handleCreateCheckout('premium', premiumBillingInterval)}
              disabled={(currentPlan === 'premium' && !isHomePage) || isLoading}
            >
              <CreditCard className="mr-2 h-4 w-4" />
              {currentPlan === 'premium' && !isHomePage ? 'Plano Atual' : (premiumBillingInterval === 'year' ? 'Assinar Plano Anual' : 'Assinar Premium')}
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-8 text-center">
        <div className="bg-gradient-to-r from-gray-800 to-gray-700 border border-gray-600 rounded-lg p-4 max-w-2xl mx-auto">
          <p className="text-green-400 font-medium">🎉 Plano Gratuito Permanente! Não é período de teste - use para sempre sem custo.</p>
        </div>
      </div>

      <div className="mt-16 bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-500/30 rounded-2xl p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Coins className="w-8 h-8 text-yellow-400 mr-3" />
            <h3 className="text-3xl font-bold text-white">Pacotes Avulsos de Créditos</h3>
          </div>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">Precisa de mais créditos? Compre pacotes avulsos de créditos!</p>
          <p className="text-yellow-400 text-sm max-w-2xl mx-auto mt-2 flex items-center justify-center">
            <span className="mr-2">⚠️</span>
            IMPORTANTE: Créditos avulsos expiram em 30 dias após a compra
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-gray-800/50 border border-gray-600 rounded-xl p-6 text-center hover:border-blue-400 transition-colors">
            <div className="mb-4">
              <h4 className="text-xl font-semibold text-white mb-2">Pacote Básico</h4>
              <div className="flex items-center justify-center mb-3">
                <Coins className="w-6 h-6 text-blue-400 mr-2" />
                <span className="text-2xl font-bold text-blue-400">100K créditos</span>
              </div>
              <div className="text-3xl font-bold text-white mb-2">R$ 15</div>
              <div className="bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full text-sm font-medium">Economia de 30%</div>
            </div>
            <Button 
              variant="outline" 
              className="w-full btn-hover-always"
              onClick={() => handleCreateCreditCheckout('basic')}
              disabled={creditLoading === 'basic'}
            >
              <CreditCard className="w-4 h-4" />
              Comprar Créditos
            </Button>
          </div>

          <div className="bg-blue-900/30 border-2 border-blue-400 rounded-xl p-6 text-center relative transform scale-105">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium whitespace-nowrap">🔥 MAIS POPULAR</span>
            </div>
            <div className="mb-4 pt-2">
              <h4 className="text-xl font-semibold text-white mb-2">Pacote Médio</h4>
              <div className="flex items-center justify-center mb-3">
                <Coins className="w-6 h-6 text-blue-300 mr-2" />
                <span className="text-2xl font-bold text-blue-300">200K créditos</span>
              </div>
              <div className="text-3xl font-bold text-white mb-2">R$ 26</div>
              <div className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-sm font-medium">Economia de 40%</div>
            </div>
            <Button 
              className="w-full bg-primary hover:bg-primary/90"
              onClick={() => handleCreateCreditCheckout('medium')}
              disabled={creditLoading === 'medium'}
            >
              <CreditCard className="w-4 h-4" />
              Comprar Créditos
            </Button>
          </div>

          <div className="bg-gray-800/50 border border-gray-600 rounded-xl p-6 text-center hover:border-purple-400 transition-colors">
            <div className="mb-4">
              <h4 className="text-xl font-semibold text-white mb-2">Pacote Grande</h4>
              <div className="flex items-center justify-center mb-3">
                <Coins className="w-6 h-6 text-purple-400 mr-2" />
                <span className="text-2xl font-bold text-purple-400">600K créditos</span>
              </div>
              <div className="text-3xl font-bold text-white mb-2">R$ 69</div>
              <div className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full text-sm font-medium">Economia de 50%</div>
            </div>
            <Button 
              variant="outline" 
              className="w-full btn-hover-always"
              onClick={() => handleCreateCreditCheckout('large')}
              disabled={creditLoading === 'large'}
            >
              <CreditCard className="w-4 h-4" />
              Comprar Créditos
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-8 bg-gradient-to-r from-purple-900/20 to-blue-900/20 border border-purple-500/30 rounded-2xl p-8">
        <div className="flex items-center justify-center mb-4">
          <Users className="w-6 h-6 text-purple-400 mr-3" />
          <h3 className="text-2xl font-bold text-white">Planos Especiais para Multiusuários e Escritórios</h3>
        </div>
        <p className="text-gray-300 text-base mb-6 max-w-3xl mx-auto leading-relaxed">Todas as vantagens do <strong className="text-white">Plano PREMIUM</strong> com recursos exclusivos para trabalho em equipe: acesso aos mesmos arquivos, montagem de documentos em conjunto e acompanhamento em tempo real para toda sua equipe ou escritório!</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <a href="mailto:contato@laib.com.br" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3">
            <Mail className="mr-2 h-4 w-4" />
            contato@laib.com.br
          </a>
          <a href="https://wa.me/5551991812486" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 bg-green-600 hover:bg-green-700 text-white px-6 py-3">
            <MessageSquare className="mr-2 h-4 w-4" />
            (51) 99181-2486
          </a>
        </div>
      </div>

      {/* Seção de Pacotes de Storage */}
      <div className="mt-16 bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-500/30 rounded-2xl p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Check className="w-8 h-8 text-purple-400 mr-3" />
            <h3 className="text-3xl font-bold text-white">Pacotes de Armazenamento</h3>
          </div>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">Aumente seu espaço de armazenamento com pacotes mensais!</p>
          <p className="text-purple-400 text-sm max-w-2xl mx-auto mt-2 flex items-center justify-center">
            <span className="mr-2">ℹ️</span>
            Storage adicional é renovado mensalmente e pode ser cancelado a qualquer momento
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-gray-800/50 border border-gray-600 rounded-xl p-6 text-center hover:border-purple-400 transition-colors">
            <div className="mb-4">
              <div className="flex items-center justify-center gap-3 mb-3">
                <HardDrive className="w-8 h-8 text-purple-400" />
                <div className="flex items-baseline">
                  <span className="text-5xl font-bold text-white">1</span>
                  <span className="text-2xl text-gray-300 ml-1">GB</span>
                </div>
              </div>
              <div className="text-3xl font-bold text-purple-400">R$99</div>
              <div className="text-sm text-gray-400 mt-1">/mês</div>
            </div>
            <p className="text-gray-300 text-sm mb-6">Ideal para usuários iniciantes</p>
            <Button 
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => handleCreateStorageCheckout('small')}
              disabled={storageLoading === 'small'}
            >
              {storageLoading === 'small' ? 'Processando...' : 'Comprar Agora'}
            </Button>
          </div>

          <div className="bg-gray-800/50 border-2 border-purple-500 rounded-xl p-6 text-center relative">
            <div className="absolute top-0 right-0 bg-purple-500 text-white px-3 py-1 text-xs font-bold rounded-bl-lg">
              POPULAR
            </div>
            <div className="mb-4 mt-2">
              <div className="flex items-center justify-center gap-3 mb-3">
                <HardDrive className="w-8 h-8 text-purple-400" />
                <div className="flex items-baseline">
                  <span className="text-5xl font-bold text-white">2</span>
                  <span className="text-2xl text-gray-300 ml-1">GB</span>
                </div>
              </div>
              <div className="text-3xl font-bold text-purple-400">R$149</div>
              <div className="text-sm text-gray-400 mt-1">/mês</div>
            </div>
            <p className="text-gray-300 text-sm mb-6">Perfeito para uso profissional</p>
            <Button 
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => handleCreateStorageCheckout('medium')}
              disabled={storageLoading === 'medium'}
            >
              {storageLoading === 'medium' ? 'Processando...' : 'Comprar Agora'}
            </Button>
          </div>

          <div className="bg-gray-800/50 border border-gray-600 rounded-xl p-6 text-center hover:border-purple-400 transition-colors">
            <div className="mb-4">
              <div className="flex items-center justify-center gap-3 mb-3">
                <HardDrive className="w-8 h-8 text-purple-400" />
                <div className="flex items-baseline">
                  <span className="text-5xl font-bold text-white">10</span>
                  <span className="text-2xl text-gray-300 ml-1">GB</span>
                </div>
              </div>
              <div className="text-3xl font-bold text-purple-400">R$799</div>
              <div className="text-sm text-gray-400 mt-1">/mês</div>
            </div>
            <p className="text-gray-300 text-sm mb-6">Máximo armazenamento disponível</p>
            <Button 
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => handleCreateStorageCheckout('large')}
              disabled={storageLoading === 'large'}
            >
              {storageLoading === 'large' ? 'Processando...' : 'Comprar Agora'}
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-12 text-center text-sm text-gray-400">
        <p>Todos os planos incluem acesso ao suporte por e-mail e atualizações regulares.</p>
        <p className="mt-2">Cancele sua assinatura a qualquer momento através da página de configurações.</p>
      </div>
    </div>
  );
};